import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

export type InvalidationDomain =
  | "planning"
  | "procurement"
  | "operations"
  | "finance"
  | "feedback"
  | "system";

type VersionMap = Record<InvalidationDomain, number>;

type InvalidationContextValue = {
  versions: VersionMap;
  markDirty: (domains: InvalidationDomain | InvalidationDomain[] | "all") => void;
};

const INITIAL_VERSIONS: VersionMap = {
  planning: 0,
  procurement: 0,
  operations: 0,
  finance: 0,
  feedback: 0,
  system: 0,
};

const InvalidationContext = createContext<InvalidationContextValue | null>(null);

export function InvalidationProvider({ children }: { children: ReactNode }) {
  const [versions, setVersions] = useState(INITIAL_VERSIONS);

  const value = useMemo<InvalidationContextValue>(
    () => ({
      versions,
      markDirty(domains) {
        setVersions((current) => {
          const next = { ...current };
          const targets =
            domains === "all"
              ? (Object.keys(current) as InvalidationDomain[])
              : Array.isArray(domains)
                ? domains
                : [domains];
          targets.forEach((domain) => {
            next[domain] = current[domain] + 1;
          });
          return next;
        });
      },
    }),
    [versions],
  );

  return <InvalidationContext.Provider value={value}>{children}</InvalidationContext.Provider>;
}

export function useInvalidation() {
  const context = useContext(InvalidationContext);
  if (!context) {
    throw new Error("useInvalidation must be used within InvalidationProvider");
  }
  return context;
}

export function useDomainVersion(domain: InvalidationDomain): number {
  return useInvalidation().versions[domain];
}
